import { useMemo, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, X, ChevronDown } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '../utils/apiClient';
import {
  DECLARED_INGEST_TIMEOUT_MS,
  formatEflexUploadMessage,
  isFuelTransactionFile,
  uploadEflexFuelTransactions,
  type EflexIngestResponse,
} from '../utils/fuelTransactionUpload';
import { fileToBase64 } from '../utils/fuelPriceUpload';

/**
 * File uploads for Declared (Day Sheet / Same Day Tracker) and Eflex fuel.
 * Flow: browser → POST /ingest/* → ZeeCore RawPayloadStore (fs or S3) → queue →
 * ingest processor → Iceberg with identity / event-id dedupe.
 */

const UPLOAD_CATEGORIES = [
  {
    id: 'daily-plan',
    label: 'Daily Plan (Day Sheet)',
    description: 'Darcica Day Sheet workbook — driver/vehicle assignments for the week.',
    accept: '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensions: ['.xlsx', '.xls'],
    kind: 'dailyPlan' as const,
  },
  {
    id: 'same-day-tracker',
    label: 'Same Day Tracker',
    description: 'Same Day Tracker CSV — declared orders for the day ledger.',
    accept: '.csv,text/csv',
    extensions: ['.csv'],
    kind: 'sameDayTracker' as const,
  },
  {
    id: 'fuel-transactions',
    label: 'Fuel Transactions',
    description: 'Keyfuels / eflex fuel card export — CSV or Excel (first sheet). Diesel Drawing rows with a registration are ingested.',
    accept: '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensions: ['.csv', '.xlsx', '.xls'],
    kind: 'fuelTransactions' as const,
  },
] as const;

type UploadCategoryId = (typeof UPLOAD_CATEGORIES)[number]['id'];

type QueuedResponse = EflexIngestResponse;

function isAcceptedForCategory(file: File, categoryId: UploadCategoryId): boolean {
  const cat = UPLOAD_CATEGORIES.find((c) => c.id === categoryId);
  if (!cat) return false;
  if (cat.kind === 'fuelTransactions') return isFuelTransactionFile(file);
  const name = file.name.toLowerCase();
  return cat.extensions.some((ext) => name.endsWith(ext));
}

export default function DataFeed() {
  const [category, setCategory] = useState<UploadCategoryId | ''>('');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedCategory = useMemo(
    () => UPLOAD_CATEGORIES.find((c) => c.id === category),
    [category],
  );

  const acceptAttr = selectedCategory?.accept
    ?? '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const stageFile = (file: File) => {
    setError(null);
    setSuccessMessage(null);
    if (!category) {
      setSelectedFile(null);
      setError('Choose a data category first, then select the file.');
      return;
    }
    if (!isAcceptedForCategory(file, category)) {
      setSelectedFile(null);
      const exts = selectedCategory?.extensions.join(', ') ?? '.csv / .xlsx';
      setError(`This category expects ${exts}. Got “${file.name}".`);
      return;
    }
    setSelectedFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setSuccessMessage(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    setError(null);
    setSuccessMessage(null);
    if (!category || !selectedCategory) {
      setError('Please choose a data category before uploading.');
      return;
    }
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }
    if (!isAcceptedForCategory(selectedFile, category)) {
      setError('Selected file does not match the chosen category format.');
      return;
    }

    setIsUploading(true);
    try {
      if (selectedCategory.kind === 'fuelTransactions') {
        const { response, json } = await uploadEflexFuelTransactions(selectedFile);
        if (!response.ok) {
          throw new Error(
            json.error
              || json.message
              || `Upload failed with HTTP ${response.status}`,
          );
        }
        const formatted = formatEflexUploadMessage(selectedFile.name, json);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (formatted.kind === 'empty') {
          setError(formatted.text);
        } else {
          setSuccessMessage(formatted.text);
        }
        return;
      }

      let url: string;
      let body: Record<string, unknown>;
      let ingestLabel: string;

      if (selectedCategory.kind === 'dailyPlan') {
        url = API_ENDPOINTS.declaredIngest.dailyPlan();
        body = {
          workbookBase64: await fileToBase64(selectedFile),
          filename: selectedFile.name,
        };
        ingestLabel = 'Same Day TMS (Daily Plan)';
      } else {
        url = API_ENDPOINTS.declaredIngest.sameDayTracker();
        body = {
          csvBase64: await fileToBase64(selectedFile),
          filename: selectedFile.name,
        };
        ingestLabel = 'Same Day TMS (Tracker)';
      }

      const response = await authenticatedFetch(
        url,
        { method: 'POST', body: JSON.stringify(body) },
        DECLARED_INGEST_TIMEOUT_MS,
      );
      const json = (await response.json().catch(() => ({}))) as QueuedResponse;

      if (!response.ok) {
        throw new Error(
          json.error
            || json.message
            || `Upload failed with HTTP ${response.status}`,
        );
      }

      if (json.status === 'empty') {
        setSuccessMessage(
          `Parsed “${selectedFile.name}” but found no rows to queue`
          + (json.parse?.stats
            ? ` (${Object.entries(json.parse.stats).map(([k, v]) => `${k}: ${v}`).join(', ')})`
            : '')
          + '.',
        );
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const headlineParts = [
        `Queued “${selectedFile.name}” for ${ingestLabel}`,
        json.week_start_date ? `week starting ${json.week_start_date}` : null,
      ].filter(Boolean);

      const statParts = [
        typeof json.items_queued === 'number' ? `${json.items_queued} item(s)` : null,
        typeof json.input_records === 'number' ? `${json.input_records} CSV row(s) read` : null,
        typeof json.skipped === 'number' && json.skipped > 0 ? `${json.skipped} skipped` : null,
      ].filter(Boolean);

      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSuccessMessage(
        statParts.length > 0
          ? `${headlineParts.join(' · ')}.\n${statParts.join(' · ')}.`
          : `${headlineParts.join(' · ')}.`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const timedOut = /timed out|TimeoutError|aborted/i.test(msg);
      setError(
        timedOut
          ? 'Upload timed out while ZeeCore parsed or queued the file. Try again, or check that ZeeCore and the raw-stored worker are running.'
          : msg,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const dropHint =
    selectedCategory?.kind === 'fuelTransactions'
      ? 'Drop a fuel CSV or Excel file here'
      : selectedCategory?.kind === 'sameDayTracker'
        ? 'Drop a Same Day Tracker CSV here'
        : selectedCategory?.kind === 'dailyPlan'
          ? 'Drop a Day Sheet Excel file here'
          : 'Drop a file here';

  return (
    <div className="space-y-8 w-full">
      <section>
        <h2 className="text-2xl font-bold text-white">Data Upload</h2>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Upload Daily Plan, Same Day Tracker, or Fuel Transactions.
        </p>
      </section>

      <section className="bg-card/70 border border-border rounded-2xl p-6 space-y-6">
        <div>
          <label htmlFor="upload-category" className="block text-sm font-medium text-muted-foreground mb-2">
            Data category <span className="text-warning">*</span>
          </label>
          <div className="relative">
            <select
              id="upload-category"
              value={category}
              disabled={isUploading}
              onChange={(e) => {
                setCategory(e.target.value as UploadCategoryId | '');
                setSelectedFile(null);
                setError(null);
                setSuccessMessage(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="w-full appearance-none bg-background border border-accent text-white rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-success/60 focus:border-success disabled:opacity-60"
            >
              <option value="">Select a category…</option>
              {UPLOAD_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
          {selectedCategory && (
            <p className="text-xs text-muted-foreground mt-2">{selectedCategory.description}</p>
          )}
          {!selectedCategory && (
            <p className="text-xs text-muted-foreground mt-2">
              Choose Daily Plan, Same Day Tracker, or Fuel Transactions.
            </p>
          )}
        </div>

        <div
          className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all ${
            isDragging
              ? 'border-success bg-success/10'
              : 'border-accent bg-background/40 hover:border-success/60 hover:bg-background/60'
          } ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) stageFile(file);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            className="hidden"
            disabled={isUploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) stageFile(file);
            }}
          />

          <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-white font-medium mb-1">{dropHint}</p>
          <p className="text-sm text-muted-foreground mb-5">
            {selectedCategory
              ? `Accepted: ${selectedCategory.extensions.join(', ')}`
              : 'Select a category to see accepted formats'}
          </p>
          <Button
            type="button"
            variant="default"
            size="md"
            disabled={isUploading || !category}
            onClick={() => fileInputRef.current?.click()}
            className="px-5 py-2.5 font-semibold hover:bg-success"
          >
            Select file
          </Button>

          {selectedFile && (
            <div className="mt-6 mx-auto max-w-md flex items-center gap-3 rounded-lg border border-accent bg-card px-4 py-3 text-left">
              <FileSpreadsheet className="w-5 h-5 text-success flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                  {selectedCategory ? ` · ${selectedCategory.label}` : ''}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                disabled={isUploading}
                onClick={clearFile}
                className="hover:bg-secondary"
                aria-label="Remove file"
              >
                <X className="w-4 h-4" aria-hidden />
              </Button>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/40 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="rounded-lg border border-success/40 bg-success/30 px-4 py-3 text-sm text-success flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="whitespace-pre-line">{successMessage}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            variant="default"
            size="md"
            loading={isUploading}
            disabled={!category || !selectedFile}
            onClick={() => void handleUpload()}
            className="px-6 py-2.5 font-semibold hover:bg-success"
          >
            Upload file
          </Button>
        </div>
      </section>
    </div>
  );
}
